import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { scrollToSection } from "./Nav";

const HERO_IMG =
  "https://images.unsplash.com/photo-1710118247573-e0bfd9f436d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxrb29sYXUlMjBtb3VudGFpbnMlMjBoYXdhaWklMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzkwMzg3NzI1fDA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} data-testid="hero-stat-counter">
      {display}
      {suffix}
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative flex min-h-screen flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Ko'olau mountain range, O'ahu, Hawai'i"
          className="h-full w-full object-cover"
          data-testid="hero-koolau-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-basalt/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-basalt via-transparent to-basalt/50" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pt-32 pb-16 lg:px-10"
      >
        <MaskedLine delay={0.5}>
          <span className="inline-flex items-center gap-3 rounded-full border border-sand/20 bg-basalt/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/80 backdrop-blur-md sm:text-xs" data-testid="hero-eyebrow">
            <ShieldCheck size={14} className="text-ochre" />
            Hale Pono — House of Righteousness
          </span>
        </MaskedLine>

        <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-sand sm:text-5xl lg:text-7xl" data-testid="hero-heading">
          <MaskedLine delay={0.65}>Out of compliance?</MaskedLine>
          <MaskedLine delay={0.8}>We secure federal</MaskedLine>
          <MaskedLine delay={0.95}>
            <span className="text-ochre">approvals. Guaranteed.</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={1.15} className="mt-8 max-w-2xl">
          <p className="text-base text-mist sm:text-lg" data-testid="hero-subheading">
            Hale Pono specializes in DOJ extensions &amp; stays, Plans of Correction, Plans of
            Implementation, and SAPS for ICF &amp; IDD facilities of every size — backed by a
            proven, 100% compliance record.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.3}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollToSection("#intake")}
              className="group flex items-center gap-3 rounded-full bg-ochre px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-basalt transition-[background-color,transform] duration-300 hover:bg-ochre-light hover:-translate-y-0.5"
              data-testid="hero-cta-intake-button"
            >
              Request Emergency Intake
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToSection("#services")}
              className="rounded-full border border-sand/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-sand transition-[border-color,background-color] duration-300 hover:border-ochre/60 hover:bg-sand/5"
              data-testid="hero-cta-services-button"
            >
              Explore Services
            </button>
          </div>
        </MaskedLine>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 border-t border-sand/15 bg-basalt/40 backdrop-blur-md"
        data-testid="hero-stats-bar"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-sand/10 px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-10">
          {[
            { value: 100, suffix: "%", label: "Compliance Record" },
            { value: 100, suffix: "%", label: "DOJ Approval Rate" },
            { value: 0, suffix: "", label: "Risk — Money-Back Guarantee", prefix: "$" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-4 py-6 sm:justify-center">
              <span className="font-display text-4xl font-extrabold text-ochre lg:text-5xl">
                {s.prefix}
                <Counter value={s.value} suffix={s.suffix} />
              </span>
              <span className="max-w-[140px] font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-mist">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
